import type {SuggestObject} from '@yandex-delivery/api/model/suggest'
import {
  Box,
  Button,
  IconButton,
  Image,
  Input,
  Lead,
  Popper,
  Slot,
  Spinner,
  Typography,
  UnstableCard as Paper,
} from '@yandex-delivery/blocks'
import {spacing} from '@yandex-delivery/blocks/theme/theme'
import {useI18n} from '@yandex-delivery/core/i18n'
import {useBinaryState, useDebouncedFunction, useMountEffect} from '@yandex-delivery/hooks'
import {tag2url} from '@yandex-delivery/utils'
import type {ReactElement} from 'react'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useController, useFormContext, useWatch} from 'react-hook-form'

import type {AddressPin} from '../../../../api/address'
import {useFetchAddressPin, useFetchSuggest} from '../../../../api/address'
import {useService} from '../../../../api/address/suggest'
import {EventGoal, useFormUserEventHandler} from '../../../../lib/metrika'
import {useSuggest} from '../../hooks/useSuggest'
import {ButtonWithMapModal} from '../ButtonWithMapModal'
import {SuggestItems} from '../SuggestItems/SuggestItems'
import * as classes from './Suggest.classes'
import type {Props} from './types'

/** Иконка в кнопке "Назад" */
const ARROW_LEFT_SLUG = 'delivery_ndd_new_flow_arrow'

/** Минимальная длина строки поиска, начиная с которой запрашиваем данные саджеста */
const MIN_SUGGEST_TEXT_LENGTH = 3

/** Ожидание до перезапроса данных саджеста после очередного изменения строки поиска */
const SUGGEST_REQUEST_DEBOUNCE_WAIT_IN_MS = 500

/**
 * Компонент с инпутом и саджестом адресов.
 */
export function Suggest({
  addressPin,
  coordinates: coordinatesProp,
  isSource,
  label,
  name,
  onClose,
  isReturnAddress = false,
  floatingItems = false,
}: Props): ReactElement {
  const i18n = useI18n()
  const {control} = useFormContext()
  const {field} = useController({name, control})
  const [isPopperOpen, openPopper, closePopper] = useBinaryState()
  const {setValue} = useFormContext()
  const isRouteOptimized = useWatch({name: 'is_route_optimized'})
  const {suggestMode} = useSuggest()
  const {fetchAddressSuggest} = useService()

  const handleReachGoal = useFormUserEventHandler()

  const coordinates =
    suggestMode === 'delivery_intercity'
      ? coordinatesProp
      : addressPin?.title
        ? addressPin?.coordinates
        : coordinatesProp

  const [inputValue, setInputValue] = useState(addressPin?.title || '')
  const [isError, setIsError] = useState(false)

  const handleSelect = useCallback(
    (newAddressPin: AddressPin) => {
      if (isRouteOptimized) {
        setValue('is_route_optimized', false)
      }
      setInputValue(newAddressPin.title)
      if (isReturnAddress) {
        field.onChange({...newAddressPin, ...field.value})
      } else {
        field.onChange(newAddressPin)
      }
      onClose?.()
      closePopper()
    },
    [closePopper, field, isReturnAddress, isRouteOptimized, onClose, setValue],
  )

  const {
    mutate: mutateSuggest,
    isPending: isSuggestPending,
    data: suggestItems,
    error: suggestError,
  } = useFetchSuggest()
  const {
    mutate: mutateAddressPin,
    isPending: isAddressPinPending,
    error: addressPinError,
  } = useFetchAddressPin({onSuccess: handleSelect})

  const getSuggestDataOnInput = useDebouncedFunction(
    useCallback(
      (searchValue: string) => {
        if (searchValue.length >= MIN_SUGGEST_TEXT_LENGTH) {
          mutateSuggest({coordinates, text: searchValue, type: isSource ? 'a' : 'b', currentMode: suggestMode})
        }
      },
      [isSource, suggestMode, coordinates, mutateSuggest],
    ),
    SUGGEST_REQUEST_DEBOUNCE_WAIT_IN_MS,
  )

  const handleInputValueChange = useCallback(
    (newValue: string) => {
      setInputValue(newValue)
      getSuggestDataOnInput(newValue)
    },
    [getSuggestDataOnInput],
  )

  const handleInputValueClear = useCallback(() => {
    setIsError(false)
    mutateSuggest({coordinates, text: '', type: isSource ? 'a' : 'b', currentMode: suggestMode})
  }, [isSource, suggestMode, coordinates, mutateSuggest])

  const handleAddressSelect = useCallback(
    async (suggestObject: SuggestObject) => {
      if (isRouteOptimized) {
        setValue('is_route_optimized', false)
      }
      handleReachGoal(
        isSource ? EventGoal.AddressFromSuggestResultItemClick : EventGoal.AddressToSuggestResultItemClick,
      )

      if (suggestMode === 'delivery_intercity') {
        const data = await fetchAddressSuggest({
          location: coordinates,
          type: isSource ? 'a' : 'b',
          __origin: suggestObject,
          currentMode: suggestMode,
        })
        if (data.type === 'final') mutateAddressPin({coordinates, suggestObject, currentMode: suggestMode})
        else {
          setInputValue(suggestObject.title.text ?? '')
          mutateSuggest({
            coordinates,
            text: suggestObject.text,
            type: isSource ? 'a' : 'b',
            currentMode: suggestMode,
          })
        }
      } else mutateAddressPin({coordinates, suggestObject})
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [coordinates, handleReachGoal, isSource, mutateAddressPin],
  )

  const handleRetryClick = useCallback(() => {
    mutateSuggest({coordinates, text: inputValue, type: isSource ? 'a' : 'b', currentMode: suggestMode})
  }, [isSource, suggestMode, coordinates, inputValue, mutateSuggest])

  const errorMessage = suggestError?.message || addressPinError?.message

  const errorBlock = useMemo(
    () => (
      <Box justifyContent="center" p="s">
        {errorMessage ? (
          <Typography color="error" justifyContent="center" variant="body2">
            {errorMessage}
          </Typography>
        ) : (
          <Button onPress={handleRetryClick} variant="error" fullWidth>
            {i18n('bdui.general-error-repeat', 'Что-то пошло не так, повторить?')}
          </Button>
        )}
      </Box>
    ),
    [errorMessage, handleRetryClick, i18n],
  )

  const rootElementRef = useRef<HTMLDivElement | null>(null)

  const isPending = isSuggestPending || isAddressPinPending

  // Запрос за адресами относительно базовых координат при загрузке саджеста
  useMountEffect(() => {
    mutateSuggest({
      coordinates,
      text: '',
      type: isSource ? 'a' : 'b',
      currentMode: suggestMode,
    })
  })

  useEffect(() => {
    setIsError(!!suggestError || !!addressPinError)
  }, [suggestError, addressPinError])

  return (
    <div ref={rootElementRef} className={classes.container}>
      {isReturnAddress ? (
        <Input
          clearable={!isPending}
          label={label}
          onChange={handleInputValueChange}
          onClear={handleInputValueClear}
          onClick={floatingItems ? openPopper : void 0}
          trail={isPending && <Spinner />}
          value={inputValue}
          variant="outline"
        />
      ) : (
        <Paper className={classes.paper} padding="S">
          <Slot size="l">
            <Lead>
              <IconButton onPress={onClose} shape="circle" size="m" variant="ghost">
                <Image src={tag2url(ARROW_LEFT_SLUG)} width={parseInt(spacing(3))} />
              </IconButton>
            </Lead>
            <Box alignItems="center" className={classes.inputContainer}>
              <Input
                className={classes.input}
                clearable={!isPending}
                label={label}
                onChange={handleInputValueChange}
                onClear={handleInputValueClear}
                onClick={floatingItems ? openPopper : void 0}
                trail={isPending && <Spinner />}
                value={inputValue}
              />
              <ButtonWithMapModal isSource={isSource} onSave={handleSelect} pinLocation={addressPin?.coordinates} />
            </Box>
          </Slot>
        </Paper>
      )}
      {floatingItems ? (
        <Popper opened={isPopperOpen} placement="bottom-start" role="presentation" target={rootElementRef}>
          <Paper className={classes.popper} padding="S">
            {suggestItems ? <SuggestItems items={suggestItems} onSelect={handleAddressSelect} /> : null}
          </Paper>
          {isError && errorBlock}
        </Popper>
      ) : (
        <>
          <Paper className={classes.itemsPaper} padding="S">
            {suggestItems ? <SuggestItems items={suggestItems} onSelect={handleAddressSelect} /> : null}
          </Paper>
          {isError && errorBlock}
        </>
      )}
    </div>
  )
}
